import { signIn, signOut, fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { config } from '../config';
import { AuthUser, LoginRequest } from '../types';

// Configure Amplify with Cognito settings
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.cognito.userPoolId,
      userPoolClientId: config.cognito.userPoolWebClientId,
    },
  },
} as any);

class AuthService {
  /**
   * Sign in user with username and password
   */
  async signIn(request: LoginRequest): Promise<AuthUser> {
    try {
      console.log('Starting sign in for user:', request.username);

      const result = await signIn({
        username: request.username,
        password: request.password,
      });

      console.log('Sign in result:', result);

      // Get session to extract tokens
      const session = await fetchAuthSession();
      console.log('Session:', session);

      const tokens = session.tokens;
      console.log('Tokens:', tokens);

      const idToken = tokens?.idToken?.toString() || '';
      console.log('ID Token:', idToken ? 'Present' : 'Missing');

      // Store tokens in localStorage
      localStorage.setItem('id_token', idToken);
      localStorage.setItem('username', request.username);

      // Extract user info directly from JWT
      const groups = this.getUserGroups(session);
      console.log('Groups:', groups);

      const userInfo = this.extractUserInfoFromToken(idToken);
      console.log('User info:', userInfo);

      return {
        username: request.username,
        userId: userInfo.sub || request.username,
        email: userInfo.email || '',
        groups,
      };
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      await signOut();
      localStorage.removeItem('id_token');
      localStorage.removeItem('username');
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      // First, try to get token from session (just logged in)
      let idToken: string | null = null;

      try {
        const session = await fetchAuthSession();
        if (session.tokens?.idToken) {
          idToken = session.tokens.idToken.toString();
        }
      } catch (e) {
        console.log('No session available, checking localStorage');
      }

      // If no session token, try localStorage (page reload after login)
      if (!idToken) {
        idToken = localStorage.getItem('id_token');
        console.log('Using token from localStorage:', idToken ? 'found' : 'not found');
      }

      if (!idToken) {
        console.log('No ID token available');
        return null;
      }

      const userInfo = this.extractUserInfoFromToken(idToken);
      const groups = this.extractGroupsFromToken(idToken);

      console.log('Current user:', userInfo.cognito_username, 'Groups:', groups);

      return {
        username: userInfo['cognito:username'] || localStorage.getItem('username') || '',
        userId: userInfo.sub || '',
        email: userInfo.email || '',
        groups,
      };
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  /**
   * Refresh ID token
   */
  async refreshToken(): Promise<string> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString() || '';
      localStorage.setItem('id_token', idToken);
      return idToken;
    } catch (error) {
      console.error('Refresh token error:', error);
      throw error;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user !== null;
  }

  /**
   * Check if user is admin (member of Admins group)
   */
  async isAdmin(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user ? user.groups.includes('Admins') : false;
  }

  /**
   * Extract groups directly from token string
   */
  private extractGroupsFromToken(tokenString: string): string[] {
    const payload = this.extractUserInfoFromToken(tokenString);
    return payload['cognito:groups'] || [];
  }

  /**
   * Extract user info from JWT token
   */
  private extractUserInfoFromToken(tokenString: string): any {
    try {
      // JWT format: header.payload.signature
      const parts = tokenString.split('.');
      if (parts.length !== 3) return {};

      // Decode payload (base64url)
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
      );

      return payload;
    } catch (error) {
      console.error('Error extracting token info:', error);
      return {};
    }
  }

  /**
   * Extract user groups from Cognito session
   * Groups are stored in cognito:groups claim in ID token
   */
  private getUserGroups(session: any): string[] {
    try {
      const idToken = session.tokens?.idToken;
      if (!idToken) return [];

      const tokenString = idToken.toString ? idToken.toString() : idToken;
      const payload = this.extractUserInfoFromToken(tokenString);

      return payload['cognito:groups'] || [];
    } catch (error) {
      console.error('Error extracting user groups:', error);
      return [];
    }
  }
}

export const authService = new AuthService();
